import type { TranscriptUpdate } from "@frontierx/shared/schemas/TranscriptUpdate";
// Import the correct types from the shared packages
import type { RecallMeetingParticipant } from "@frontierx/types";
import { Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import useLocalStorageState from "use-local-storage-state";
import { Button } from "@/components/ui/button";
import type { SmoothScrollAreaElement } from "@/components/ui/smooth-scroll-area";
import { SmoothScrollArea } from "@/components/ui/smooth-scroll-area";
import {
  CompressedTable,
  CompressedTableBody,
  CompressedTableCell,
  CompressedTableRow,
} from "@/components/ui/table-compressed";
import { useGroupedTranscript } from "../../hooks/useGroupedTranscript";
import type { TranscriptSentence } from "../../utils/transcriptProcessor";
import { useCallContext } from "../Call/CallContext";
import { ParticipantsDialog } from "../Call/ParticipantsDialog";
import { CloseTabButton } from "../CloseTabButton";
import {
  type ParticipantRoleFilter,
  ParticipantRoleFilterDropdown,
} from "../Filter/ParticipantRoleFilterDropdown";
import { LatencyDisplay } from "../LatencyDisplay";
import { PanelHeader } from "../PanelHeader";

export interface Speaker {
  initials: string;
  name: string;
  id: number;
  color: string;
}

export function TranscriptTable() {
  const { handleGetAIHelp, transcript, participants, participantRoles } =
    useCallContext();

  return (
    <TranscriptTableUI
      handleGetAIHelp={handleGetAIHelp}
      transcript={transcript}
      participants={participants}
      participantRoles={participantRoles}
    />
  );
}

export function TranscriptTableUI({
  transcript,
  participants,
  participantRoles,
  handleGetAIHelp,
}: {
  transcript: TranscriptUpdate[];
  participants: RecallMeetingParticipant[];
  participantRoles: {
    assignments: Array<{
      recallParticipantId: string;
      role: "salesperson" | "client" | "observer";
      displayName: string;
      isCurrentUser?: boolean;
      assignedBy?: string;
      assignedAt?: string;
    }>;
    participants: Array<{
      id: number;
      name: string;
      role: "salesperson" | "client" | "observer";
      isHost: boolean;
      hasExplicitAssignment: boolean;
      assignedAt?: string;
    }>;
  } | null;
  handleGetAIHelp: (args: {
    question: string;
    transcriptItemIds: string[];
  }) => void;
}) {
  const scrollAreaRef = useRef<SmoothScrollAreaElement>(null);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [userScrolled, setUserScrolled] = useState(false);

  const handleGetSentenceHelp = useCallback(
    (sentence: TranscriptSentence) => {
      handleGetAIHelp({
        question: sentence.sentence,
        transcriptItemIds: sentence.item_ids,
      });
    },
    [handleGetAIHelp],
  );

  const handleWheel = useCallback(() => {
    if (!userScrolled) {
      setUserScrolled(true);
      setIsAutoScroll(false);
    }
  }, [userScrolled]);

  // Auto-scroll to bottom when new messages arrive
  // biome-ignore lint/correctness/useExhaustiveDependencies: Auto-scroll behavior should only depend on transcript changes
  useEffect(() => {
    if (isAutoScroll && scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector(
        "[data-radix-scroll-area-viewport]",
      );
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [isAutoScroll, transcript.length]);

  // Reset auto-scroll when user reaches the bottom
  useEffect(() => {
    if (!userScrolled) {
      return;
    }

    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) {
      return;
    }

    const scrollElement = scrollArea.querySelector(
      "[data-radix-scroll-area-viewport]",
    );
    if (!scrollElement) {
      return;
    }

    const checkIfAtBottom = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      if (isAtBottom && userScrolled) {
        setIsAutoScroll(true);
        setUserScrolled(false);
      }
    };

    checkIfAtBottom();
    scrollElement.addEventListener("scroll", checkIfAtBottom);

    return () => {
      scrollElement.removeEventListener("scroll", checkIfAtBottom);
    };
  }, [userScrolled]);

  const getSpeakerInitials = (name: string): string => {
    if (!name?.trim()) return "??";
    return name
      .trim()
      .split(" ")
      .filter((word) => word.length > 0)
      .map((word) => word.charAt(0))
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  // Process the transcript into sentences first
  const processedTranscript = useGroupedTranscript(transcript);

  // Build speakers object from processed transcript
  const speakers = processedTranscript.reduce(
    (acc, item) => {
      if (!acc[item.speaker_id]) {
        const initials = getSpeakerInitials(item.speaker);
        const count = Object.values(acc).filter((s) =>
          s.initials.startsWith(initials),
        ).length;
        acc[item.speaker_id] = {
          initials: count > 0 ? `${initials}${count + 1}` : initials,
          name: item.speaker,
          id: item.speaker_id,
          color: `hsl(${(item.speaker_id * 137) % 360}, 70%, 40%)`,
        };
      }
      return acc;
    },
    {} as Record<number, Speaker>,
  );

  const [displayFilter, setDisplayFilter] =
    useLocalStorageState<ParticipantRoleFilter>("transcript-display-filter", {
      defaultValue: "customer",
    });

  // Helper function to get participant role
  const getParticipantRole = (
    speakerId: number,
  ): "salesperson" | "customer" | "observer" | "unknown" => {
    const participantInfo = participantRoles?.participants.find(
      (p) => p.id === speakerId,
    );
    if (participantInfo) {
      return participantInfo.role === "client"
        ? "customer"
        : participantInfo.role;
    }

    // Fallback to host/guest logic for unassigned participants
    const participant = participants.find((p) => p.id === speakerId);
    if (!participant) {
      return "unknown";
    }

    return participant.is_host ? "salesperson" : "customer";
  };

  // Filter the processed transcript based on participant roles
  const filteredTranscript = processedTranscript.filter((item) => {
    if (displayFilter === "all") {
      return true;
    }

    const participantRole = getParticipantRole(item.speaker_id);
    return participantRole === displayFilter;
  });

  // Use the filtered transcript directly (no need to call useGroupedTranscript again)
  const groupedTranscript = filteredTranscript;

  return (
    <div className="flex h-full w-full flex-col">
      <PanelHeader title="Transcript">
        <div className="flex flex-row items-center justify-between space-x-2">
          <ParticipantsDialog participantCount={participants.length} />
          <div className="flex-grow-0">
            <ParticipantRoleFilterDropdown
              value={displayFilter}
              onSelect={setDisplayFilter}
            />
          </div>
          <div className="flex-grow-0">
            <CloseTabButton />
          </div>
        </div>
      </PanelHeader>
      <LatencyDisplay />

      <SmoothScrollArea
        ref={scrollAreaRef}
        className="h-full flex-grow"
        smoothScroll
        onWheel={handleWheel}
      >
        <CompressedTable>
          <CompressedTableBody>
            {groupedTranscript.map((sentence) => (
              <TranscriptTableItem
                speakers={speakers}
                key={sentence.id}
                sentence={sentence}
                handleGetSentenceHelp={handleGetSentenceHelp}
              />
            ))}
          </CompressedTableBody>
        </CompressedTable>
      </SmoothScrollArea>
    </div>
  );
}

interface TranscriptTableItemProps {
  speakers: Record<number, Speaker>;
  sentence: TranscriptSentence;
  handleGetSentenceHelp: (sentence: TranscriptSentence) => void;
}

function TranscriptTableItem({
  speakers,
  sentence,
  handleGetSentenceHelp,
}: Readonly<TranscriptTableItemProps>) {
  const speaker = speakers[sentence.speaker_id];
  const [isHovered, setIsHovered] = useState(false);

  // Fallback for when speaker object is undefined
  const fallbackSpeaker: Speaker = {
    initials: sentence.speaker
      ? sentence.speaker.slice(0, 2).toUpperCase()
      : "??",
    name: sentence.speaker || "Unknown",
    id: sentence.speaker_id,
    color: `hsl(${(sentence.speaker_id * 137) % 360}, 70%, 40%)`,
  };

  const displaySpeaker = speaker || fallbackSpeaker;

  const handleSentenceClick = () => {
    handleGetSentenceHelp(sentence);
  };

  const formattedText = sentence.sentence.replace(/\n/g, " ");

  return (
    <CompressedTableRow
      className="group hover:bg-blue-50"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CompressedTableCell className="relative w-16 border-r">
        <div className="flex items-center justify-center">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium text-white"
            style={{ backgroundColor: displaySpeaker?.color }}
          >
            {displaySpeaker?.initials}
          </div>
        </div>
      </CompressedTableCell>
      <CompressedTableCell className="flex-1 p-3 relative">
        <div className="flex items-center">
          <span className="text-sm leading-relaxed pr-10">{formattedText}</span>
          {isHovered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSentenceClick}
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <Sparkles size={16} />
            </Button>
          )}
        </div>
      </CompressedTableCell>
    </CompressedTableRow>
  );
}
